import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, PickSelect } from "@/components/apply/Field";
import { COUNTRIES, TIMEZONES, CATEGORIES } from "@/data/options";
import { useSiteSettings } from "@/context/SettingsContext";
import { api, formatErr } from "@/lib/api";

const DIALS = [...new Set(COUNTRIES.map((c) => c.dial))];
export const EMPTY_MEMBER = {
  full_name: "", email: "", phone_code: "+1", phone: "", country: "", city: "", timezone: "America/Los_Angeles",
  business_name: "", category: "", tier: "bronze", custom_tier: { name: "", price: "", intros: "" }, intro_limit: "",
  payment_amount: "", payment_date: "", status: "Active Member", notes: "", send_welcome: true,
};

export const memberToForm = (m) => ({
  ...EMPTY_MEMBER, ...m.contact, business_name: m.profile?.business_name || "", category: m.profile?.category || "",
  tier: m.tier, custom_tier: m.custom_tier || EMPTY_MEMBER.custom_tier, intro_limit: m.intro_limit || "",
  payment_amount: m.payment?.amount || "", payment_date: m.payment?.date || "", id: m.id,
});

const payload = (f) => ({
  ...f,
  intro_limit: f.intro_limit ? Number(f.intro_limit) : null,
  payment_amount: f.payment_amount ? Number(f.payment_amount) : null,
  custom_tier: f.tier === "custom" ? { name: f.custom_tier.name, price: Number(f.custom_tier.price || 0), intros: Number(f.custom_tier.intros || 1) } : null,
});

export const MemberDialog = ({ member, onClose, onSaved }) => {
  const { settings } = useSiteSettings();
  const [f, setF] = useState(member);
  const editing = !!member?.id;
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  const setCt = (k) => (e) => setF((p) => ({ ...p, custom_tier: { ...p.custom_tier, [k]: e.target.value } }));
  const tierOpts = [...(settings?.tiers || []).map((t) => ({ value: t.key, label: `${t.name} (${t.intros} intros)` })), { value: "custom", label: "Custom tier" }];

  const save = async () => {
    try {
      if (editing) {
        await api.put(`/admin/members/${f.id}`, payload(f));
        toast.success("Member updated");
        onSaved(null);
      } else {
        const { data } = await api.post("/admin/members", payload(f));
        toast.success(data.welcome_sent ? "Member created - welcome email sent" : "Member created silently");
        onSaved(data.setup_link);
      }
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="member-dialog">
        <DialogHeader><DialogTitle>{editing ? "Edit member" : "Add member"}</DialogTitle></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Full name"><Input value={f.full_name} onChange={set("full_name")} className="h-11 rounded-xl" data-testid="member-name-input" /></Field>
          <Field label="Email"><Input type="email" value={f.email} onChange={set("email")} className="h-11 rounded-xl" data-testid="member-email-input" /></Field>
          <Field label="Phone">
            <div className="flex gap-2">
              <div className="w-28 shrink-0"><PickSelect value={f.phone_code} onChange={set("phone_code")} options={DIALS} testId="member-phone-code-select" /></div>
              <Input value={f.phone} onChange={set("phone")} className="h-12 rounded-xl" data-testid="member-phone-input" />
            </div>
          </Field>
          <Field label="Country"><PickSelect value={f.country} onChange={set("country")} options={COUNTRIES.map((c) => c.name)} testId="member-country-select" /></Field>
          <Field label="City" optional><Input value={f.city} onChange={set("city")} className="h-11 rounded-xl" data-testid="member-city-input" /></Field>
          <Field label="Time zone"><PickSelect value={f.timezone} onChange={set("timezone")} options={TIMEZONES} testId="member-timezone-select" /></Field>
          <Field label="Business name" optional><Input value={f.business_name} onChange={set("business_name")} className="h-11 rounded-xl" data-testid="member-business-input" /></Field>
          <Field label="Buyer category"><PickSelect value={f.category} onChange={set("category")} options={CATEGORIES} testId="member-category-select" /></Field>
          <Field label="Tier"><PickSelect value={f.tier} onChange={set("tier")} options={tierOpts} testId="member-tier-select" /></Field>
          <Field label="Intros allowed" hint="Leave blank to use the tier default."><Input type="number" min={1} value={f.intro_limit} onChange={set("intro_limit")} className="h-11 rounded-xl" data-testid="member-intro-limit-input" /></Field>
          {f.tier === "custom" && (
            <div className="sm:col-span-2 grid gap-3 sm:grid-cols-3 rounded-xl border border-purple-500/40 bg-purple-500/5 p-3" data-testid="member-custom-tier">
              <Input placeholder="Custom tier name" value={f.custom_tier.name} onChange={setCt("name")} className="h-10 rounded-xl" data-testid="member-custom-tier-name" />
              <Input type="number" placeholder="Price (USD)" value={f.custom_tier.price} onChange={setCt("price")} className="h-10 rounded-xl" data-testid="member-custom-tier-price" />
              <Input type="number" placeholder="Introductions" value={f.custom_tier.intros} onChange={setCt("intros")} className="h-10 rounded-xl" data-testid="member-custom-tier-intros" />
            </div>
          )}
          <Field label="Cash payment amount" optional><Input type="number" value={f.payment_amount} onChange={set("payment_amount")} className="h-11 rounded-xl" data-testid="member-payment-amount-input" /></Field>
          <Field label="Payment date" optional><Input type="date" value={f.payment_date} onChange={set("payment_date")} className="h-11 rounded-xl" data-testid="member-payment-date-input" /></Field>
          {!editing && (
            <>
              <Field label="Membership status"><PickSelect value={f.status} onChange={set("status")} options={["Active Member", "Suspended"]} testId="member-status-select" /></Field>
              <label className="flex items-center justify-between gap-3 rounded-xl border border-border p-3 self-end">
                <span className="text-sm font-bold">Send welcome email with portal login</span>
                <Switch checked={f.send_welcome} onCheckedChange={set("send_welcome")} data-testid="member-send-welcome-toggle" />
              </label>
              <div className="sm:col-span-2"><Field label="Internal notes" optional><Textarea rows={2} value={f.notes} onChange={set("notes")} className="rounded-xl" data-testid="member-notes-input" /></Field></div>
            </>
          )}
        </div>
        <button onClick={save} className="h-11 rounded-full bg-primary text-primary-foreground font-bold" data-testid="member-save-button">{editing ? "Save changes" : "Create member"}</button>
      </DialogContent>
    </Dialog>
  );
};
